import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import PartnerRestaurants from './PartnerRestaurants';
import PartnerNGOs from './PartnerNGOs';
import TransportNetwork from './TransportNetwork';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';
import AboutUs from './AboutUs';
import HowItWorks from './HowItWorks';
import RestaurantDashboard from './RestaurantDashboard';
import VolunteerNetwork from './VolunteerNetwork';
import VolunteerDashboard from './VolunteerDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import NGOHome from './NGOHome';
import GlobalHeader from './components/GlobalHeader';
import GlobalFooter from './components/GlobalFooter';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './components/ui/dialog';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkExistingAuth();
  }, []);

  const checkExistingAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.log('No existing auth found');
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, sessionToken) => {
    setUser(userData);
    // Store session token in cookie (handled by backend)
  };

  const redirectToDashboard = (user, navigate) => {
    if (user.role === 'restaurant') {
      navigate('/restaurant-dashboard');
    } else if (user.role === 'ngo') {
      navigate('/ngo-dashboard');
    } else if (user.role === 'volunteer') {
      navigate('/volunteer-dashboard');
    } else if (user.role === 'super_admin') {
      navigate('/admin/super');
    } else {
      // For transporter, admin, and other roles - redirect to landing page for now
      // TODO: Implement dedicated dashboards for these roles
      navigate('/');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Join Us Section Component
const JoinUsSection = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('restaurant');

  const sections = {
    restaurant: {
      title: 'Restaurant',
      icon: '🏪',
      content: (
        <div className="space-y-6">
          <p className="text-lg text-gray-700">
            Turn your surplus food into community impact. Join hundreds of restaurants already making a difference through Food Connect.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🍽️</span>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Quick Logging</h4>
                <p className="text-gray-600">Log in your surplus food easily with quantity, type, and pickup time.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚡</span>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Smart Matching</h4>
                <p className="text-gray-600">Our AI instantly connects your surplus to nearby NGOs.</p>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <Button
              size="lg"
              onClick={() => navigate('/auth')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
            >
              Join Now
            </Button>
          </div>
        </div>
      )
    },
    ngo: {
      title: 'NGO and Community Associations',
      icon: '🏠',
      content: (
        <div className="space-y-6">
          <p className="text-lg text-gray-700">
            Serve more people with fresh, quality food — safely and sustainably.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔔</span>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Instant Notifications</h4>
                <p className="text-gray-600">Get alerts when nearby restaurants list surplus meals.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🚚</span>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Reliable Deliveries</h4>
                <p className="text-gray-600">Verified volunteers deliver directly to your NGO with full tracking.</p>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <Button
              size="lg"
              onClick={() => navigate('/auth')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
            >
              Join Now
            </Button>
          </div>
        </div>
      )
    },
    volunteer: {
      title: 'Volunteer',
      icon: '🤝',
      content: (
        <div className="space-y-6">
          <p className="text-lg text-gray-700">
            Drive positive lasting impact by helping move rescued food from kitchens to communities.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📦</span>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Flexible Opportunities</h4>
                <p className="text-gray-600">Choose delivery requests that fit your route and schedule.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">📍</span>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Real-Time Tracking</h4>
                <p className="text-gray-600">Manage pickups and drop-offs easily through our app.</p>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <Button
              size="lg"
              onClick={() => navigate('/auth')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
            >
              Join Now
            </Button>
          </div>
        </div>
      )
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
          Join Us
        </h2>
        
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:items-center">
          {/* Left Column - Navigation */}
          <div className="lg:col-span-1 space-y-3">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`w-full text-left px-6 py-4 rounded-lg transition-all duration-300 ${
                  activeSection === key
                    ? 'bg-green-600 text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{section.icon}</span>
                  <span className="font-semibold">{section.title}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Right Column - Content Panel */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg p-8 min-h-[300px] transition-all duration-500 shadow-2xl border border-gray-200">
              {sections[activeSection].content}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      if (user.role === 'restaurant') {
        navigate('/restaurant-dashboard');
      } else if (user.role === 'ngo') {
        navigate('/ngo-dashboard');
      } else if (user.role === 'volunteer') {
        navigate('/volunteer-dashboard');
      } else if (user.role === 'super_admin') {
        navigate('/admin/super');
      } else {
        // For transporter, admin, and other roles - stay on landing page for now
        // TODO: Implement dedicated dashboards for these roles
        // navigate('/'); // Don't redirect, let them stay on landing page
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">

      {/* Hero Section */}
      <section 
        className="py-20 px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/home-banner.png)' }}
      >
        {/* Background overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        
        <div className="container mx-auto max-w-6xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
            Connecting Surplus to
            <span className="text-green-400 block">Smiles</span>
          </h1>
          <p className="text-xl text-white mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
            Food Connect is a seamless platform where restaurant surplus, volunteer drivers, and NGOs come together to deliver fresh meals to underprivileged and migrant communities across India and Singapore.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')} 
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-3 shadow-lg"
              data-testid="hero-get-started-btn"
            >
              Be a Part of the Change
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold mb-2">1,500+</div>
              <div className="text-green-100">Meals Rescued</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">28+</div>
              <div className="text-green-100">Restaurants</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">13+</div>
              <div className="text-green-100">NGOs</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">150+</div>
              <div className="text-green-100">Volunteers</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">7,500+</div>
              <div className="text-green-100">Lives Nourished</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">99%</div>
              <div className="text-green-100">On-Time Delivery</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works section moved to dedicated /how-it-works page */}

      {/* Join Us Section - Two Column Accordion Layout */}
      <JoinUsSection />

      {/* Restaurants Section */}
      {/* Terms and Conditions Section removed - available via dedicated page */}

      {/* Privacy Policy Section moved to dedicated page */}

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
};

// Simple Authentication Page
const AuthPage = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const navigate = useNavigate();
  const { login } = useAuth();

  // Simple form handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData, {
        withCredentials: true
      });
      
      if (response.data.status === 'success') {
        login(response.data.user);
        toast.success('Welcome back!');
        if (response.data.user.role === 'restaurant') {
          navigate('/restaurant-dashboard');
        } else if (response.data.user.role === 'ngo') {
          navigate('/ngo-dashboard');
        } else if (response.data.user.role === 'volunteer') {
          navigate('/volunteer-dashboard');
        } else if (response.data.user.role === 'super_admin') {
          navigate('/admin/super');
        } else {
          // For transporter, admin, and other roles - redirect to landing page for now
          // TODO: Implement dedicated dashboards for these roles
          navigate('/');
        }
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed. Please check your credentials.');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (formData, businessLicenseFile) => {
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('phone', formData.phone || '');
      submitData.append('role', formData.role);
      submitData.append('address', formData.address);
      submitData.append('city', formData.city);
      submitData.append('country', formData.country);
      submitData.append('latitude', formData.latitude || 0);
      submitData.append('longitude', formData.longitude || 0);
      
      if (businessLicenseFile) {
        submitData.append('business_license', businessLicenseFile);
      }
      
      const response = await axios.post(`${API}/auth/register`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Registration successful! Please login with your new account.');
      setActiveTab('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      console.error('Registration error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <img 
              src="/logo.png" 
              alt="Food Connect Logo" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-2xl font-bold text-gray-800">Food Connect</span>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={loginData.email}
                  onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  data-testid="email-input"
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  data-testid="password-input"
                />
                <Button 
                  type="submit" 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register" className="space-y-4">
              <RegistrationForm 
                onSubmit={handleRegister} 
                initialData={{}}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// Terms and Conditions Modal Component
const TermsModal = ({ isOpen, onClose, onAccept }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-green-700">Terms and Conditions</DialogTitle>
          <DialogDescription>
            Please read and accept our terms and conditions to continue with registration.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-3 text-sm">
            <h3 className="font-semibold text-lg">1. Food Connect Service Agreement</h3>
            <p className="text-gray-600">
              By registering with Food Connect, you agree to use our platform responsibly to connect surplus food 
              with those in need. Our service facilitates food rescue operations across India and Singapore.
            </p>
            
            <h3 className="font-semibold text-lg">2. User Responsibilities</h3>
            <div className="space-y-2 text-gray-600">
              <p><strong>Restaurants:</strong> Ensure all surplus food is safe, properly stored, and accurately described. Confirm food safety standards are met.</p>
              <p><strong>NGOs:</strong> Accept food donations responsibly and distribute them safely to intended beneficiaries.</p>
              <p><strong>Transporters:</strong> Handle food items with care, maintain hygiene standards, and complete deliveries promptly.</p>
            </div>
            
            <h3 className="font-semibold text-lg">3. Food Safety and Quality</h3>
            <p className="text-gray-600">
              All users must comply with local food safety regulations. Food donors must confirm safety standards, 
              and recipients must inspect food upon delivery. Food Connect facilitates connections but users are 
              responsible for food quality and safety.
            </p>
            <p className="text-gray-600">
              From the moment the food is accepted, whether by an NGO or a volunteer, full responsibility for its safe and timely handling and delivery rests with that party until it is transferred to the next designated partner.
            </p>
            
            <h3 className="font-semibold text-lg">4. Privacy and Data</h3>
            <p className="text-gray-600">
              We collect location data, contact information, and usage data to improve matching and service delivery. 
              Your data is protected according to applicable privacy laws and will not be shared without consent.
            </p>
            
            <h3 className="font-semibold text-lg">5. Liability</h3>
            <p className="text-gray-600">
              Food Connect serves as a platform to facilitate food rescue. Users participate at their own risk and 
              responsibility. We are not liable for food quality, safety issues, or delivery problems.
            </p>
            
            <h3 className="font-semibold text-lg">6. Community Guidelines</h3>
            <p className="text-gray-600">
              Maintain respectful communication, provide accurate information, and use the platform solely for 
              legitimate food rescue purposes. Misuse of the platform may result in account suspension.
            </p>
            
            <h3 className="font-semibold text-lg">7. Service Availability</h3>
            <p className="text-gray-600">
              Currently available in India and Singapore. Service features may vary by location. We reserve the 
              right to modify or discontinue services with appropriate notice.
            </p>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="terms-decline-btn">
            Decline
          </Button>
          <Button 
            onClick={onAccept} 
            className="bg-green-600 hover:bg-green-700"
            data-testid="terms-accept-btn"
          >
            Accept and Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Registration Form Component
const RegistrationForm = ({ onSubmit, initialData }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    password: '',
    phone: initialData?.phone || '',
    role: '',
    address: '',
    city: '',
    country: 'India',
    latitude: 0,
    longitude: 0
  });
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [businessLicense, setBusinessLicense] = useState(null);
  const [licenseError, setLicenseError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setLicenseError('');
    
    if (!file) {
      setBusinessLicense(null);
      return;
    }
    
    // Validate file type (only JPEG and PNG)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setLicenseError('Only JPEG and PNG files are allowed');
      e.target.value = '';
      setBusinessLicense(null);
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setLicenseError('File size must be less than 5MB');
      e.target.value = '';
      setBusinessLicense(null);
      return;
    }
    
    setBusinessLicense(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password || !formData.role || !formData.address || !formData.city) {
      toast.error('Please fill all required fields including email and password');
      return;
    }
    
    if (!businessLicense) {
      toast.error('Please attach your business license');
      return;
    }
    
    if (!isPasswordValid) {
      toast.error('Please ensure your password meets all security requirements');
      return;
    }
    
    if (!termsAccepted) {
      setShowTerms(true);
      return;
    }
    
    setLoading(true);
    
    // Get location if possible
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const updatedFormData = {
            ...formData,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          onSubmit(updatedFormData, businessLicense);
          setLoading(false);
        },
        () => {
          // Fallback without location
          onSubmit(formData, businessLicense);
          setLoading(false);
        }
      );
    } else {
      onSubmit(formData, businessLicense);
      setLoading(false);
    }
  };

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTerms(false);
    toast.success('Terms accepted. Please submit your registration.');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        type="text"
        placeholder="Name *"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        required
        data-testid="name-input"
      />
      
      <Input
        type="email"
        placeholder="Email *"
        value={formData.email}
        onChange={(e) => handleChange('email', e.target.value)}
        required
        data-testid="email-input"
      />

      <Input
        type="password"
        placeholder="Password *"
        value={formData.password}
        onChange={(e) => handleChange('password', e.target.value)}
        required
        minLength={8}
        data-testid="password-reg-input"
      />
      
      <PasswordStrengthIndicator
        password={formData.password}
        onValidationChange={setIsPasswordValid}
      />
      
      {!initialData?.phone && (
        <Input
          type="tel"
          placeholder="Phone (optional)"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          data-testid="phone-reg-input"
        />
      )}

      <select
        value={formData.role}
        onChange={(e) => handleChange('role', e.target.value)}
        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        required
        data-testid="role-select"
      >
        <option value="">Select Role *</option>
        <option value="restaurant">Restaurant</option>
        <option value="ngo">NGO</option>
        <option value="volunteer">Volunteer</option>
        {/* Transporter and Admin options removed from public registration */}
      </select>

      <Input
        type="text"
        placeholder="Address *"
        value={formData.address}
        onChange={(e) => handleChange('address', e.target.value)}
        required
        data-testid="address-input"
      />

      <Input
        type="text"
        placeholder="City *"
        value={formData.city}
        onChange={(e) => handleChange('city', e.target.value)}
        required
        data-testid="city-input"
      />

      <select
        value={formData.country}
        onChange={(e) => handleChange('country', e.target.value)}
        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        required
        data-testid="country-select"
      >
        <option value="India">India</option>
        <option value="Singapore">Singapore</option>
      </select>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Attach Business License * (JPEG/PNG, max 5MB)
        </label>
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          required
          data-testid="business-license-input"
        />
        {businessLicense && (
          <div className="text-sm text-green-600 flex items-center gap-2">
            <span>✓</span>
            <span>{businessLicense.name} ({(businessLicense.size / 1024).toFixed(2)} KB)</span>
          </div>
        )}
        {licenseError && (
          <div className="text-sm text-red-600 flex items-center gap-2">
            <span>✗</span>
            <span>{licenseError}</span>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <input
          type="checkbox"
          id="terms-checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="rounded"
          data-testid="terms-checkbox"
        />
        <label htmlFor="terms-checkbox" className="text-sm text-gray-700">
          I agree to the{' '}
          <button
            type="button"
            onClick={() => setShowTerms(true)}
            className="text-green-600 hover:text-green-700 underline"
            data-testid="terms-link"
          >
            Terms and Conditions
          </button>
        </label>
      </div>

      <Button 
        type="submit" 
        className="w-full bg-green-600 hover:bg-green-700"
        disabled={loading}
        data-testid="register-submit-btn"
      >
        {loading ? 'Creating Account...' : 'Create Account'}
      </Button>

      <TermsModal 
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onAccept={handleTermsAccept}
      />
    </form>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// Dashboard Component (DEPRECATED - Only kept for reference, route removed)
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // This component is no longer accessible via routing

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
  };

  const getRoleSpecificTabs = () => {
    const baseTabs = ['overview', 'matches'];
    
    switch (user.role) {
      case 'restaurant':
        return [...baseTabs, 'surplus'];
      case 'ngo':
        return [...baseTabs, 'demands'];
      case 'transporter':
        return [...baseTabs, 'deliveries'];
      case 'admin':
        return [...baseTabs, 'analytics', 'users'];
      default:
        return baseTabs;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Food Connect Logo" 
                className="h-10 w-auto"
              />
            </div>
            <Badge variant="secondary" className="capitalize">
              {user.role}
            </Badge>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, {user.name}</span>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 lg:grid-cols-6 w-full mb-8">
            {getRoleSpecificTabs().map(tab => (
              <TabsTrigger 
                key={tab} 
                value={tab} 
                className="capitalize"
                data-testid={`${tab}-tab`}
              >
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab user={user} />
          </TabsContent>

          <TabsContent value="matches">
            <MatchesTab user={user} />
          </TabsContent>

          {user.role === 'restaurant' && (
            <TabsContent value="surplus">
              <SurplusTab user={user} />
            </TabsContent>
          )}

          {user.role === 'ngo' && (
            <TabsContent value="demands">
              <DemandsTab user={user} />
            </TabsContent>
          )}

          {user.role === 'transporter' && (
            <TabsContent value="deliveries">
              <DeliveriesTab user={user} />
            </TabsContent>
          )}

          {user.role === 'admin' && (
            <>
              <TabsContent value="analytics">
                <AnalyticsTab user={user} />
              </TabsContent>
              <TabsContent value="users">
                <UsersTab user={user} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
};

// Dashboard Tab Components
const OverviewTab = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading overview...</div>;
  }

  return (
    <div className="space-y-6" data-testid="overview-content">
      <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">Total Food Connectd</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.total_food_kg || 0} kg</div>
            <p className="text-xs text-gray-500">{stats?.total_meals || 0} meals equivalent</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">Total Deliveries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats?.total_deliveries || 0}</div>
            <p className="text-xs text-gray-500">Successful rescues</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">Your Role</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600 capitalize">{user.role}</div>
            <p className="text-xs text-gray-500">{user.city}, {user.country}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {user.role === 'restaurant' && (
              <Button className="h-20 flex flex-col" data-testid="quick-add-surplus">
                <span className="text-lg mb-1">🍽️</span>
                <span>Add Surplus Food</span>
              </Button>
            )}
            {user.role === 'ngo' && (
              <Button className="h-20 flex flex-col" data-testid="quick-add-demand">
                <span className="text-lg mb-1">📋</span>
                <span>Post Food Need</span>
              </Button>
            )}
            {user.role === 'transporter' && (
              <Button className="h-20 flex flex-col" data-testid="quick-view-routes">
                <span className="text-lg mb-1">🚛</span>
                <span>View My Routes</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const MatchesTab = ({ user }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`, {
        withCredentials: true
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading matches...</div>;
  }

  return (
    <div className="space-y-6" data-testid="matches-content">
      <h2 className="text-2xl font-bold text-gray-900">Food Matches</h2>
      
      <div className="grid gap-6">
        {matches.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-gray-500">No matches found</p>
            </CardContent>
          </Card>
        ) : (
          matches.map((match) => (
            <Card key={match.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">
                      {match.surplus?.name || 'Food Item'}
                    </CardTitle>
                    <CardDescription>
                      Match Score: {match.match_score}% • Distance: {match.distance_km} km
                    </CardDescription>
                  </div>
                  <Badge 
                    variant={match.status === 'delivered' ? 'default' : 'secondary'}
                    className={match.status === 'delivered' ? 'bg-green-600' : ''}
                  >
                    {match.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-sm text-gray-600 mb-2">Restaurant</h4>
                    <p className="font-medium">{match.restaurant?.name}</p>
                    <p className="text-sm text-gray-500">{match.restaurant?.address}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-600 mb-2">NGO</h4>
                    <p className="font-medium">{match.ngo?.name}</p>
                    <p className="text-sm text-gray-500">{match.ngo?.address}</p>
                  </div>
                </div>
                
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge variant="outline">
                    {match.surplus?.food_type}
                  </Badge>
                  <Badge variant="outline">
                    {match.surplus?.quantity} {match.surplus?.unit}
                  </Badge>
                  {match.transporter && (
                    <Badge variant="outline">
                      Transporter: {match.transporter.name}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const SurplusTab = ({ user }) => {
  const [surplus, setSurplus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchSurplus();
  }, []);

  const fetchSurplus = async () => {
    try {
      const response = await axios.get(`${API}/surplus`, {
        withCredentials: true
      });
      setSurplus(response.data);
    } catch (error) {
      console.error('Failed to fetch surplus:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSurplus = async (formData) => {
    try {
      await axios.post(`${API}/surplus`, formData, {
        withCredentials: true
      });
      toast.success('Surplus food added successfully');
      setShowForm(false);
      fetchSurplus();
    } catch (error) {
      toast.error('Failed to add surplus food');
      console.error('Add surplus error:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading surplus food...</div>;
  }

  return (
    <div className="space-y-6" data-testid="surplus-content">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Surplus Food</h2>
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-surplus-btn"
        >
          Add Surplus Food
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add Surplus Food</CardTitle>
          </CardHeader>
          <CardContent>
            <SurplusForm onSubmit={handleAddSurplus} onCancel={() => setShowForm(false)} />
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6">
        {surplus.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-gray-500">No surplus food logged yet</p>
            </CardContent>
          </Card>
        ) : (
          surplus.map((item) => (
            <Card key={item.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{item.name}</CardTitle>
                    <CardDescription>{item.description}</CardDescription>
                  </div>
                  <Badge 
                    variant={item.status === 'delivered' ? 'default' : 'secondary'}
                    className={item.status === 'delivered' ? 'bg-green-600' : ''}
                  >
                    {item.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Type:</span>
                    <p className="capitalize">{item.food_type}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Quantity:</span>
                    <p>{item.quantity} {item.unit}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Ready:</span>
                    <p>{new Date(item.ready_time).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Pickup:</span>
                    <p>{new Date(item.pickup_time).toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="mt-4 flex items-center space-x-2">
                  {item.safety_confirmed && (
                    <Badge className="bg-green-100 text-green-800">✓ Safety Confirmed</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const SurplusForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    food_type: 'veg',
    quantity: '',
    unit: 'meals',
    ready_time: '',
    pickup_time: '',
    best_before: '',
    safety_confirmed: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.quantity || !formData.ready_time || !formData.pickup_time || !formData.best_before) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const submitData = {
      ...formData,
      quantity: parseFloat(formData.quantity),
      ready_time: new Date(formData.ready_time).toISOString(),
      pickup_time: new Date(formData.pickup_time).toISOString(),
      best_before: new Date(formData.best_before).toISOString()
    };
    
    onSubmit(submitData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          placeholder="Food item name *"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
          data-testid="surplus-name-input"
        />
        
        <select
          value={formData.food_type}
          onChange={(e) => handleChange('food_type', e.target.value)}
          className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          data-testid="surplus-type-select"
        >
          <option value="veg">Vegetarian</option>
          <option value="non-veg">Non-Vegetarian</option>
        </select>
      </div>
      
      <Input
        placeholder="Description (optional)"
        value={formData.description}
        onChange={(e) => handleChange('description', e.target.value)}
        data-testid="surplus-description-input"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          type="number"
          placeholder="Quantity *"
          value={formData.quantity}
          onChange={(e) => handleChange('quantity', e.target.value)}
          required
          data-testid="surplus-quantity-input"
        />
        
        <select
          value={formData.unit}
          onChange={(e) => handleChange('unit', e.target.value)}
          className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          data-testid="surplus-unit-select"
        >
          <option value="meals">Meals</option>
          <option value="kg">Kilograms</option>
        </select>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ready Time *</label>
          <Input
            type="datetime-local"
            value={formData.ready_time}
            onChange={(e) => handleChange('ready_time', e.target.value)}
            required
            data-testid="surplus-ready-time-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time *</label>
          <Input
            type="datetime-local"
            value={formData.pickup_time}
            onChange={(e) => handleChange('pickup_time', e.target.value)}
            required
            data-testid="surplus-pickup-time-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Best Before *</label>
          <Input
            type="datetime-local"
            value={formData.best_before}
            onChange={(e) => handleChange('best_before', e.target.value)}
            required
            data-testid="surplus-best-before-input"
          />
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="safety-confirmed"
          checked={formData.safety_confirmed}
          onChange={(e) => handleChange('safety_confirmed', e.target.checked)}
          className="rounded"
          data-testid="surplus-safety-checkbox"
        />
        <label htmlFor="safety-confirmed" className="text-sm text-gray-700">
          I confirm this food is safe for consumption
        </label>
      </div>
      
      <div className="flex space-x-4">
        <Button 
          type="submit" 
          className="bg-green-600 hover:bg-green-700"
          data-testid="surplus-submit-btn"
        >
          Add Surplus Food
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          data-testid="surplus-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

const DemandsTab = ({ user }) => {
  // Similar implementation for NGO demands
  return (
    <div className="space-y-6" data-testid="demands-content">
      <h2 className="text-2xl font-bold text-gray-900">Food Demands</h2>
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-gray-500">Demands management coming soon...</p>
        </CardContent>
      </Card>
    </div>
  );
};

const DeliveriesTab = ({ user }) => {
  // Implementation for transporter deliveries
  return (
    <div className="space-y-6" data-testid="deliveries-content">
      <h2 className="text-2xl font-bold text-gray-900">My Deliveries</h2>
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-gray-500">Deliveries management coming soon...</p>
        </CardContent>
      </Card>
    </div>
  );
};

const AnalyticsTab = ({ user }) => {
  // Implementation for admin analytics
  return (
    <div className="space-y-6" data-testid="analytics-content">
      <h2 className="text-2xl font-bold text-gray-900">Analytics</h2>
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-gray-500">Advanced analytics coming soon...</p>
        </CardContent>
      </Card>
    </div>
  );
};

const UsersTab = ({ user }) => {
  // Implementation for admin user management
  return (
    <div className="space-y-6" data-testid="users-content">
      <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-gray-500">User management coming soon...</p>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Registration Page
const AdminRegPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    address: '',
    city: '',
    country: 'India',
    role: 'admin',
    latitude: 0,
    longitude: 0,
    admin_secret_code: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password || !formData.admin_secret_code || !formData.address || !formData.city) {
      toast.error('Please fill all required fields including email, password, and the admin secret code');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    
    try {
      // Get location if possible
      const finalFormData = { ...formData };
      
      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              finalFormData.latitude = position.coords.latitude;
              finalFormData.longitude = position.coords.longitude;
              resolve();
            },
            () => resolve() // Continue without location
          );
        });
      }
      
      const response = await axios.post(`${API}/auth/admin/register`, finalFormData);
      toast.success('Admin account created successfully!');
      navigate('/auth');
      
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Invalid admin secret code');
      } else if (error.response?.status === 400) {
        toast.error('Admin account already exists with this email/phone');
      } else {
        toast.error('Failed to create admin account');
      }
      console.error('Admin registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">FC</span>
            </div>
            <span className="text-2xl font-bold text-gray-800">Admin Access</span>
          </div>
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            <strong>⚠️ Restricted Access</strong><br />
            Admin registration requires a secret code
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="Admin Name *"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              data-testid="admin-name-input"
            />
            
            <Input
              type="email"
              placeholder="Email *"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              data-testid="admin-email-input"
            />
            
            <Input
              type="tel"
              placeholder="Phone (optional)"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              data-testid="admin-phone-input"
            />

            <Input
              type="text"
              placeholder="Address *"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              required
              data-testid="admin-address-input"
            />

            <Input
              type="text"
              placeholder="City *"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              required
              data-testid="admin-city-input"
            />

            <select
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
              data-testid="admin-country-select"
            >
              <option value="India">India</option>
              <option value="Singapore">Singapore</option>
            </select>

            <select
              value={formData.role || 'admin'}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
              data-testid="admin-role-select"
            >
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>

            <Input
              type="password"
              placeholder="Password *"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              required
              minLength={6}
              data-testid="admin-password-input"
            />

            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <Input
                type="password"
                placeholder="Admin Secret Code *"
                value={formData.admin_secret_code}
                onChange={(e) => handleChange('admin_secret_code', e.target.value)}
                required
                className="border-yellow-300 focus:ring-yellow-500"
                data-testid="admin-secret-input"
              />
              <p className="text-xs text-yellow-700 mt-1">
                Contact system administrator for the secret code
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-red-600 hover:bg-red-700"
              disabled={loading}
              data-testid="admin-register-btn"
            >
              {loading ? 'Creating Admin Account...' : 'Create Admin Account'}
            </Button>
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/auth')}
                className="text-gray-600 hover:text-red-600 text-sm underline"
                data-testid="back-to-login-btn"
              >
                Back to User Login
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Not Found Component
const NotFound = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="not-found-page">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="text-8xl font-bold text-green-600 mb-4">404</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
        <p className="text-gray-600 mb-6">The page you are looking for does not exist or has been moved.</p>
        <a href="/" className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium">
          Go Home
        </a>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <GlobalHeader />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/admin-register" element={<AdminRegPage />} />
            <Route path="/partner-restaurants" element={<PartnerRestaurants />} />
            <Route path="/partner-ngos" element={<PartnerNGOs />} />
            <Route path="/transport-network" element={<TransportNetwork />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/volunteer-network" element={<VolunteerNetwork />} />
            <Route path="/volunteer-dashboard" element={<VolunteerDashboard />} />
            <Route path="/admin/super" element={<SuperAdminDashboard />} />
            <Route path="/restaurant-dashboard" element={<RestaurantDashboard />} />
            <Route path="/ngo-dashboard" element={<NGOHome />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
